import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store";
import { AppData, ApiError } from "./types/appdata-types";
import { SearchState, SearchSuggestion } from "./types/search-types";

const API_URL = process.env.REACT_APP_API_URL;

function filterSectionByCountry(section: any, countryId: number): any {
  if (!section || !section.plans) return section;

  const filteredPlans = (section.plans as any[]).filter(
    (plan: any) =>
      Array.isArray(plan.countries) && plan.countries.includes(countryId),
  );

  const visibleCategoryIds = new Set(
    filteredPlans.map((p: any) => p.category_id),
  );

  const filteredCategories = (section.categories as any[]).filter((cat: any) =>
    visibleCategoryIds.has(cat.id),
  );

  const visibleProviderIds = new Set(
    filteredCategories.map((cat: any) => cat.provider_id),
  );

  const filteredProviders = (section.providers as any[]).filter((p: any) =>
    visibleProviderIds.has(p.id),
  );

  return {
    ...section,
    providers: filteredProviders,
    categories: filteredCategories,
    plans: filteredPlans,
  };
}

function applyCountryFilter(rawData: AppData, countryId: number): AppData {
  const result: AppData = {};

  for (const key of Object.keys(rawData)) {
    const section = rawData[key];

    if (!section || typeof section !== "object" || !section.plans) {
      result[key] = section;
      continue;
    }

    result[key] = filterSectionByCountry(section, countryId);
  }

  return result;
}

const extractSearchSuggestions = (
  searchData: AppData,
  query: string,
): SearchSuggestion[] => {
  const suggestions: SearchSuggestion[] = [];

  if (!searchData) return suggestions;

  const categories = [
    { key: "most_popular", title: "Most Popular Today" },
    { key: "topup", title: "Direct Game Topup" },
    { key: "game_cards", title: "Game Cards" },
    { key: "gift_cards", title: "Gift Cards" },
    { key: "dg_coins", title: "DG Coins" },
    { key: "data", title: "Data Services" },
    { key: "airtime", title: "Airtime Services" },
    { key: "cable", title: "Cable TV" },
    { key: "electric", title: "Electric Payment" },
  ];

  categories.forEach(({ key, title }) => {
    if (searchData[key]?.providers) {
      searchData[key].providers.forEach((provider: any) => {
        if (provider.name?.toLowerCase().includes(query.toLowerCase())) {
          suggestions.push({
            id: `provider-${provider.name}-${key}`,
            name: provider.name,
            searchValue: provider.name,
            category: title,
            categoryKey: key,
            image_url: provider.image_url,
            type: "provider",
          });
        }

        if (provider.plans) {
          provider.plans.forEach((plan: any) => {
            if (plan.name?.toLowerCase().includes(query.toLowerCase())) {
              suggestions.push({
                id: `plan-${provider.name}-${plan.name}-${key}`,
                name: `${provider.name} - ${plan.name}`,
                searchValue: plan.name,
                category: title,
                categoryKey: key,
                image_url: provider.image_url,
                type: "plan",
              });
            }
          });
        }
      });
    }
  });

  return suggestions
    .filter(
      (suggestion, index, self) =>
        index === self.findIndex((s) => s.id === suggestion.id),
    )
    .slice(0, 8);
};

const initialState: SearchState = {
  isSearching: false,
  searchQuery: "",
  searchResults: {},
  searchSuggestions: [],
  error: null,
};

export const fetchSearchResults = createAsyncThunk<
  { query: string; results: AppData; suggestions: SearchSuggestion[] },
  string,
  { rejectValue: ApiError; state: RootState }
>("search/fetchSearchResults", async (query: string, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  const selectedCountryId = thunkAPI.getState().locale.selectedCountry?.id ?? 1;

  try {
    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${API_URL}/app-data/search`, {
      params: { query },
      headers,
    });

    const parsedData =
      typeof response.data.data === "string"
        ? JSON.parse(response.data.data)
        : response.data.data;

    const filteredResults = applyCountryFilter(parsedData, selectedCountryId);
    const suggestions = extractSearchSuggestions(filteredResults, query);

    return {
      query,
      results: filteredResults,
      suggestions,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Search failed" },
    );
  }
});

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    clearSearch: (state) => {
      state.isSearching = false;
      state.searchQuery = "";
      state.searchResults = {};
      state.searchSuggestions = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSearchResults.pending, (state) => {
        state.isSearching = true;
        state.error = null;
      })
      .addCase(fetchSearchResults.fulfilled, (state, action) => {
        state.isSearching = false;
        state.searchQuery = action.payload.query;
        state.searchResults = action.payload.results;
        state.searchSuggestions = action.payload.suggestions;
      })
      .addCase(fetchSearchResults.rejected, (state, action) => {
        state.isSearching = false;
        state.error = action.payload?.message || "Search failed";
        state.searchSuggestions = [];
      });
  },
});

export const { clearSearch } = searchSlice.actions;
export default searchSlice.reducer;
